import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  CardWrapper, SectionTitle, Field, ButtonOutlined, formatBRL,
} from '../../UI';
import { LANCAMENTOS_FINANCEIROS, SALDOS } from '../../../mocks/processos.mock';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Financeira.
 *
 * Print `grid_check_financeira.png`: Ficha Financeira no topo, Lançamentos no
 * meio, e três cards no rodapé (Saldos Atuais, Totais Já Pagos, Operações).
 */
function CardResumo({ titulo, icone, linhas }) {
  return (
    <CardWrapper>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
      }}
      >
        {icone}
        <Typography variant="h3">{titulo}</Typography>
      </Box>
      {linhas.map(l => (
        <Box
          key={l.label}
          sx={{
            display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.4,
          }}
        >
          <Typography variant="body2">{l.label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: C.navy }}>{l.valor}</Typography>
        </Box>
      ))}
    </CardWrapper>
  );
}
CardResumo.propTypes = { titulo: PropTypes.string, icone: PropTypes.node, linhas: PropTypes.array };
CardResumo.defaultProps = { titulo: null, icone: null, linhas: [] };

export default function AbaFinanceira({ processo }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 0.8vw, 22px)' }}>
      <CardWrapper>
        <SectionTitle>Ficha Financeira</SectionTitle>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: 'clamp(10px, 0.8vw, 22px)',
            mb: 2,
          }}
        >
          <Field label="Cliente:" value={processo?.nomeCliente || ''} InputProps={{ readOnly: true }} />
          <Field label="Devedor:" value={processo?.nomeDevedor || ''} InputProps={{ readOnly: true }} />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 0.8fr)' },
            gap: 'clamp(10px, 0.8vw, 22px)',
          }}
        >
          <Field label="Responsável:" value={processo?.negociador || ''} InputProps={{ readOnly: true }} />
          <Field label="Juros Simples - Repasse ao Cliente (%)" value="1,00" />
          <Field label="Repasse ao Cliente Único:" value="Não" tipo="select" options={['Sim', 'Não']} />
        </Box>
      </CardWrapper>

      <CardWrapper>
        <SectionTitle>Lançamentos</SectionTitle>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Histórico</TableCell>
                <TableCell align="right">Devedor</TableCell>
                <TableCell align="right">Cliente</TableCell>
                <TableCell align="right">H.B</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {LANCAMENTOS_FINANCEIROS.map((l, i) => (
                <TableRow key={`${l.data}-${l.historico}`} hover sx={{ backgroundColor: i === 0 ? C.paleGrey : undefined }}>
                  <TableCell>{l.data}</TableCell>
                  <TableCell>{l.historico}</TableCell>
                  <TableCell align="right">{formatBRL(l.devedor)}</TableCell>
                  <TableCell align="right">{formatBRL(l.cliente)}</TableCell>
                  <TableCell align="right">{formatBRL(l.hb)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </CardWrapper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          alignItems: 'start',
        }}
      >
        <CardResumo
          titulo="Saldos Atuais"
          icone={<BarChartIcon sx={{ color: C.navy }} />}
          linhas={[
            { label: 'Devedor:', valor: formatBRL(SALDOS.atuais.devedor) },
            { label: 'Cliente:', valor: formatBRL(SALDOS.atuais.cliente) },
            { label: 'H.B.:', valor: formatBRL(SALDOS.atuais.hb) },
          ]}
        />
        <CardResumo
          titulo="Totais Já Pagos"
          icone={<AttachMoneyIcon sx={{ color: C.navy }} />}
          linhas={[
            { label: 'Devedor:', valor: formatBRL(SALDOS.pagos.devedor) },
            { label: 'Cliente:', valor: formatBRL(SALDOS.pagos.cliente) },
            { label: 'HB:', valor: formatBRL(SALDOS.pagos.hb) },
          ]}
        />
        <CardWrapper>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
          }}
          >
            <SettingsOutlinedIcon sx={{ color: C.navy }} />
            <Typography variant="h3">Operações</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ButtonOutlined startIcon={<ArticleOutlinedIcon />}>C/C CLIENTE</ButtonOutlined>
            <ButtonOutlined startIcon={<GroupOutlinedIcon />}>C/C COMISSIONADO</ButtonOutlined>
            <ButtonOutlined startIcon={<PersonOutlineIcon />}>DEMONSTRATIVO POR TÍTULO</ButtonOutlined>
          </Box>
        </CardWrapper>
      </Box>
    </Box>
  );
}
AbaFinanceira.propTypes = { processo: PropTypes.object };
AbaFinanceira.defaultProps = { processo: null };
